/**
 * DOCUMENTATION:
 * Component: DesignPatternsStructural
 * Purpose: Teaching the Adapter and Decorator design patterns.
 * * * Note for Junior Developers:
 * - Adapter: The "Legacy Wrapper." Use this when you have a modern system that needs to
 * talk to an old API without rewriting the whole codebase.
 * - Decorator: The "Wrapping Present." Use this to add new features to an object
 * dynamically without cluttering up your base class with a million 'if' statements.
 * - Both patterns help satisfy the Open/Closed Principle (SOLID).
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

/* ✅ Main Component */
export default function DesignPatternsStructural() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const patterns = useMemo(() => [
        {
            name: "Adapter",
            intent: "Allows objects with incompatible interfaces to collaborate. It acts as a middleman that converts the interface of a class into another interface clients expect.",
            useCase: "Integrating a 3rd-party library that uses XML into your JSON-based frontend."
        },
        {
            name: "Decorator",
            intent: "Attach new behaviors to objects by placing these objects inside special wrapper objects that contain the behaviors.",
            useCase: "Adding 'Logger' or 'Validator' functionality to a base data service without changing the service's code."
        }
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. The Adapter Pattern (API Translation)",
            code: `// ✅ Modern interface expects { user: 'name', id: 1 }\n// ❌ Legacy API returns [1, 'name']\n\nclass LegacyUserAPI {\n  getOldUser() { return [502, "Kwame"]; }\n}\n\nclass UserAdapter {\n  constructor(legacyAPI) {\n    this.legacyAPI = legacyAPI;\n  }\n\n  // Translates the messy array into the clean object the UI expects\n  getUser() {\n    const [id, name] = this.legacyAPI.getOldUser();\n    return { id, user: name };\n  }\n}\n\n// Usage:\nconst adapter = new UserAdapter(new LegacyUserAPI());\nconsole.log(adapter.getUser()); // { id: 502, user: 'Kwame' }`,
            desc: "The Adapter is your best friend during a refactor. It allows you to introduce new patterns and cleaner data structures while still supporting legacy systems that you aren't ready to delete yet."
        },
        {
            title: "2. The Decorator Pattern (Composition)",
            code: `// ✅ Base Component: A simple message sender\nconst BasicSender = (message) => {\n  console.log(\`Sending: \${message}\`);\n};\n\n// ✅ Decorator: Adds logging functionality\nconst withLogging = (sender) => {\n  return (message) => {\n    console.log(\`[LOG] Timestamp: \${new Date().toISOString()}\`);\n    sender(message);\n  };\n};\n\n// ✅ Decorator: Adds encryption functionality\nconst withEncryption = (sender) => {\n  return (message) => {\n    const secret = \`*** \${message} ***\`;\n    sender(secret);\n  };\n};\n\n// Usage: Chain them together!\nconst secureLogger = withLogging(withEncryption(BasicSender));\nsecureLogger("Hello ProDev");`,
            desc: "In React, we often see this pattern implemented as Higher-Order Components (HOCs). It follows the 'Composition over Inheritance' rule, allowing you to stack behaviors like LEGO bricks."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    STRUCTURAL PATTERNS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Designing Relationships
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Structural patterns explain how to assemble objects and classes into larger structures while keeping these structures flexible and efficient. They are particularly useful for making independent libraries or legacy code work together seamlessly.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Key Structural Patterns</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {patterns.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                        <Typography variant="subtitle1" sx={{ color: "goldenrod", fontWeight: 800 }}>{item.name}</Typography>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{item.intent}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>USE CASE: {item.useCase}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>



                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/design-patterns-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ backgroundColor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px", "&:hover": { backgroundColor: "#e0b200" } }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
